import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });

    if (typeof window !== 'undefined' && (window as any).Sentry) {
      (window as any).Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  public render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div
          className="flex flex-col items-center justify-center min-h-[300px] p-8 text-center"
          role="alert"
          aria-live="assertive"
        >
          <div className="w-12 h-12 rounded-full border-2 border-red-500/40 flex items-center justify-center mb-4">
            <span className="text-red-400 text-xl">!</span>
          </div>
          <h2 className="text-sm font-semibold text-[color:var(--c-gray-300)] mb-2">
            Component Error
          </h2>
          <p className="text-xs text-[color:var(--c-gray-500)] mb-4 max-w-md">
            {this.state.error?.message || 'An unexpected error occurred in this section.'}
          </p>
          <button
            onClick={this.handleReset}
            className="px-4 py-2 text-xs text-[color:var(--c-gray-300)] border border-[color:rgb(var(--c-ink-rgb)/0.08)] rounded hover:border-[color:rgb(var(--c-ink-rgb)/0.12)] hover:text-[color:var(--c-text)] transition-colors"
          >
            Retry
          </button>
          {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
            <details className="mt-4 text-left max-w-lg">
              <summary className="text-[10px] text-[color:var(--c-gray-600)] cursor-pointer">
                Stack trace (dev only)
              </summary>
              <pre className="mt-2 text-[9px] font-mono text-[color:var(--c-gray-700)] overflow-auto max-h-48 p-2 border border-[color:rgb(var(--c-ink-rgb)/0.06)] rounded bg-[color:var(--c-panel-raised)]">
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
